import { Check } from 'lucide-react'
import { cn } from '../../lib/cn.js'

export interface PollShare {
  id: string
  label: string
  count: number
  /** Marked as the right answer. Left off where the question has none. */
  correct?: boolean
}

export interface PollResultsProps {
  shares: PollShare[]
  /**
   * How many were asked, not how many answered.
   *
   * The two are different and the difference is the point: six of twenty is a
   * quiet room, six of six is a room that has finished. Sizing the bars against
   * the answers received would draw both identically.
   */
  asked?: number
  className?: string
}

/**
 * How a room answered, as bars.
 *
 * *"Per question: the split as bars, with the correct option marked where there is
 * one."* `BarChart` cannot do this and should not learn to: it is seven vertical
 * columns for a week of takings, with no proportion, no labels beside the bars and
 * no notion of one column being the right one. This is horizontal, labelled, in
 * percentages, and read while a class is running.
 *
 * **The correct answer is marked whether or not it won.** A tick only on the
 * tallest bar would tell a host what they already know; the useful reading is when
 * the tick is on a short one.
 *
 * Nought is a hairline rather than nothing, so an option no one picked still has a
 * row and can still be seen to have been offered.
 */
export function PollResults({ shares, asked, className }: PollResultsProps) {
  const answered = shares.reduce((sum, s) => sum + s.count, 0)
  // Against those asked where we know it, so the bars stay honest in a quiet room.
  const denominator = Math.max(asked ?? answered, 1)

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {shares.map((share) => {
        const percent = Math.round((share.count / denominator) * 100)
        return (
          <div key={share.id}>
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <span
                className={cn(
                  'text-second',
                  share.correct ? 'font-semibold text-ink' : 'text-ink-2',
                )}
              >
                {share.correct && (
                  <Check
                    className="mr-1 inline size-4 text-success"
                    aria-label="The right answer"
                  />
                )}
                {share.label}
              </span>
              <span className="shrink-0 text-caption text-ink-muted tabular-nums">
                {share.count} · {percent}%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-sunk">
              <div
                className={cn('h-full rounded-full', share.correct ? 'bg-success' : 'bg-coral')}
                // A hairline at nought: the option was offered and nobody took it,
                // which is not the same as the option not existing.
                style={{ width: `${Math.max(percent, 1)}%` }}
                role="img"
                aria-label={`${share.label}: ${share.count} of ${denominator}`}
              />
            </div>
          </div>
        )
      })}
      {asked !== undefined && (
        <p className="text-caption text-ink-muted">
          {answered} of {asked} answered
        </p>
      )}
    </div>
  )
}
